import { useCallback, useState } from 'react';
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from 'recoil';
import { fetchDownloadDicomImageBySOP } from '~/backend/connector-api/connector-components';
import { StructureSet } from '~/domain/patient/structureset';
import { AvailableStructureSetView } from '~/domain/projections/views';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import useBusyIndicator from '~/hooks/useBusyIndicator';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { primaryImageState } from '~/state/atoms/image/imageAtoms';
import { sessionDataAtom } from '~/state/atoms/sessionAtoms';
import {
    currentStructureSetState,
    structureSetStateFamily,
} from '~/state/atoms/structureSet/structureSetAtoms';

type PendingSelection = {
    set: AvailableStructureSetView;
};

export default function useStructureSetSelection({
    onSelect,
}: {
    onSelect?: () => void;
}) {
    const { setEnvironmentStructureSet, initializeEnvironment } =
        useViewportEnvironment();
    const { processImportedFiles } = useProcessDicomFiles();
    const patient = useSelectedPatient();
    const sessionData = useRecoilValue(sessionDataAtom);
    const { showBusy, hideBusy } = useBusyIndicator();
    const setCurrentStructureSet = useSetRecoilState(currentStructureSetState);
    const primaryImage = useRecoilValue(primaryImageState);

    const [pendingSelection, setPendingSelection] =
        useState<PendingSelection | null>(null);

    const handleSelectLoadedStructureSet = useRecoilCallback(
        ({ snapshot }) =>
            async (structureSet: StructureSet) => {
                onSelect?.();
                const cachedStructureSet = await snapshot.getPromise(
                    structureSetStateFamily(structureSet.dicomUid)
                );
                if (cachedStructureSet) {
                    setEnvironmentStructureSet(
                        [cachedStructureSet],
                        patient?.dataSet.PatientID ?? ''
                    );
                } else {
                    setEnvironmentStructureSet(
                        [structureSet],
                        patient?.dataSet.PatientID ?? ''
                    );
                }
            },
        [onSelect, setCurrentStructureSet, setEnvironmentStructureSet, patient]
    );

    const handleSelectSessionStructureSet = useCallback(
        async (id: string) => {
            const downloadedStructureSet = patient?.structureSets.find(
                (structureSet) => structureSet.dicomUid === id
            );
            if (downloadedStructureSet) {
                handleSelectLoadedStructureSet(downloadedStructureSet);
            } else if (sessionData?.payload?.patient?.id) {
                onSelect?.();
                await showBusy('Downloading structure set...');
                try {
                    const structureSetBlob = await fetchDownloadDicomImageBySOP(
                        {
                            pathParams: {
                                patientID: sessionData.payload.patient.id,
                                modality: 'RTSTRUCT',
                                sopInstanceUID: id,
                            },
                        }
                    );
                    const files = [
                        new File(
                            [structureSetBlob],
                            `${sessionData.payload.context_structure_set.sop_instance_uid}.dcm`
                        ),
                    ];
                    const processedItems = await processImportedFiles(files);
                    if (processedItems) {
                        initializeEnvironment(processedItems);
                    }
                } finally {
                    hideBusy();
                }
            }
        },
        [
            patient,
            sessionData,
            onSelect,
            showBusy,
            hideBusy,
            processImportedFiles,
            initializeEnvironment,
            handleSelectLoadedStructureSet,
        ]
    );

    const executeSelection = useCallback(
        (set: AvailableStructureSetView) => {
            if (set.structureSetId) {
                const ss = patient?.getStructureSetById(set.structureSetId);
                if (ss) {
                    handleSelectLoadedStructureSet(ss);
                }
            } else {
                handleSelectSessionStructureSet(set.sopInstanceUid);
            }
        },
        [
            handleSelectLoadedStructureSet,
            handleSelectSessionStructureSet,
            patient,
        ]
    );

    const handleStructureSetChange = useCallback(
        (set: AvailableStructureSetView) => {
            const requiresImageChange =
                primaryImage &&
                set.imageSeriesUid &&
                set.imageSeriesUid !== primaryImage.seriesUid;

            if (requiresImageChange) {
                setPendingSelection({ set });
            } else {
                executeSelection(set);
            }
        },
        [primaryImage, executeSelection]
    );

    const confirmImageChange = useCallback(() => {
        if (pendingSelection) {
            executeSelection(pendingSelection.set);
            setPendingSelection(null);
        }
    }, [pendingSelection, executeSelection]);

    const cancelImageChange = useCallback(() => {
        setPendingSelection(null);
    }, []);

    return {
        handleSelectLoadedStructureSet,
        handleSelectSessionStructureSet,
        handleStructureSetChange,
        imageChangeConfirmation: {
            open: pendingSelection !== null,
            onConfirm: confirmImageChange,
            onCancel: cancelImageChange,
        },
    };
}
